import {
  runProgress,
  type SyncChangeOp,
  type SyncRun,
  type SyncRunChanges,
  type SyncRunDetail,
  type SyncRunEvent,
  type SyncRunMailboxStatus,
} from "@kontaktfix/shared";
import {
  Badge,
  Button,
  Card,
  cn,
  Dialog,
  Input,
  Skeleton,
  Stat,
  StatusDot,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
} from "@kontaktfix/ui";
import { ArrowLeftIcon, MagnifyingGlassIcon, StopIcon } from "@phosphor-icons/react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Page } from "@/components/page";
import { api, qs } from "@/lib/api";
import { toastError } from "@/lib/errors";
import { formatDateTime, formatNumber, locale } from "@/lib/i18n";
import { useDebounced } from "./contacts";

const POLL_MS = 2000;

/** "45 s", "3 Min. 5 s", "1 h 12 Min." */
export function formatDuration(t: TFunction, seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return t("sync.run.duration.s", { s });
  if (s < 3600) return t("sync.run.duration.ms", { m: Math.floor(s / 60), s: s % 60 });
  return t("sync.run.duration.hm", { h: Math.floor(s / 3600), m: Math.floor((s % 3600) / 60) });
}

const secondsBetween = (from: string | null, to: string | null) =>
  from ? ((to ? new Date(to).getTime() : Date.now()) - new Date(from).getTime()) / 1000 : 0;

export type RunTone = "accent" | "danger" | "success" | "neutral";

/** Bar and dot color of a run: running, failed, stopped, done. */
export function runTone(run: SyncRun): RunTone {
  if (run.status === "running") return "accent";
  if (run.status === "failed") return "danger";
  if (run.status === "cancelled") return "neutral";
  return "success";
}

/** Stops a running run after asking; `stop` calls the customer or the operator endpoint. */
export function StopRunButton({ stop, compact }: { stop: () => Promise<unknown>; compact?: boolean }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await stop();
      await client.invalidateQueries();
      setOpen(false);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button
        size={compact ? "sm" : undefined}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <StopIcon />
        {t("sync.run.stop")}
      </Button>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t("sync.run.stopTitle")}
        description={t("sync.run.stopText")}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button variant="danger" onClick={confirm} loading={busy}>
              {t("sync.run.stop")}
            </Button>
          </>
        }
      />
    </>
  );
}

export function ProgressBar({
  percent,
  tone = "accent",
  className,
}: {
  percent: number;
  tone?: RunTone;
  className?: string;
}) {
  return (
    <div className={cn("h-1.5 overflow-hidden rounded-full bg-muted", className)}>
      <div
        className={cn(
          "h-full rounded-full transition-all",
          tone === "danger"
            ? "bg-danger"
            : tone === "success"
              ? "bg-success"
              : tone === "neutral"
                ? "bg-fg-subtle"
                : "bg-accent",
        )}
        style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
      />
    </div>
  );
}

/** What the run row and the detail page say about progress. */
export function RunProgressText({ run }: { run: SyncRun }) {
  const { t } = useTranslation();
  const { remainingMinutes } = runProgress(run);
  const m = run.mailboxes;
  const total = m.queued + m.running + m.done + m.failed + m.superseded + m.cancelled;
  return (
    <span className="text-[12px] text-fg-muted tabular-nums">
      {t("sync.run.mailboxesDone", { done: m.done + m.failed + m.superseded + m.cancelled, total })}
      {run.planned > 0 &&
        ` · ${t("sync.run.writes", { done: formatNumber(run.processed), total: formatNumber(run.planned) })}`}
      {run.status === "running" &&
        remainingMinutes !== null &&
        ` · ${t("sync.remaining", { count: remainingMinutes })}`}
    </span>
  );
}

const MAILBOX_TONE: Record<SyncRunMailboxStatus, "neutral" | "accent" | "success" | "danger"> = {
  queued: "neutral",
  running: "accent",
  done: "success",
  failed: "danger",
  superseded: "neutral",
  cancelled: "neutral",
};

export function SyncRunPage() {
  const { t } = useTranslation();
  const { runId } = useParams({ from: "/app/sync/runs/$runId" });
  const search = useSearch({ from: "/app/sync/runs/$runId" });
  const navigate = useNavigate({ from: "/sync/runs/$runId" });
  const tab = search.tab ?? "mailboxes";
  const { data } = useQuery({
    queryKey: ["sync-run", runId],
    queryFn: () => api.get<SyncRunDetail>(`/api/sync/runs/${runId}`),
    refetchInterval: (q) => (q.state.data?.run.status === "running" ? POLL_MS : false),
  });

  if (!data) {
    return (
      <Page>
        <Skeleton className="mb-6 h-10 w-64" />
        <Skeleton className="h-40" />
      </Page>
    );
  }
  const { run, mailboxes } = data;
  const { percent } = runProgress(run);
  const running = run.status === "running";
  const calls = mailboxes.reduce((n, m) => n + m.calls, 0);
  const throttled = mailboxes.reduce((n, m) => n + m.throttledSeconds, 0);
  const photos = mailboxes.reduce((n, m) => n + m.done.photos, 0);

  return (
    <Page>
      <Link
        to="/sync"
        className="mb-4 inline-flex items-center gap-1.5 text-[13px] text-fg-muted hover:text-fg"
      >
        <ArrowLeftIcon className="size-4" />
        {t("sync.title")}
      </Link>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">{t("sync.run.title")}</h1>
        <Badge>{t(`sync.triggers.${run.trigger}`)}</Badge>
        <span className="flex items-center gap-1.5 text-[13px] text-fg-muted">
          <StatusDot tone={run.status === "done" && run.errors ? "warning" : runTone(run)} pulse={running} />
          {t(`sync.run.status.${run.status}`)}
        </span>
        {running && (
          <span className="ml-auto">
            <StopRunButton stop={() => api.post(`/api/sync/runs/${run.id}/cancel`)} />
          </span>
        )}
        <span className="text-[13px] text-fg-muted">
          {formatDateTime(run.startedAt)} · {formatDuration(t, secondsBetween(run.startedAt, run.finishedAt))}
        </span>
      </div>

      <Card className="mb-6 p-5">
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <span className="text-2xl font-semibold tabular-nums">{percent}%</span>
          <RunProgressText run={run} />
        </div>
        <ProgressBar percent={percent} tone={runTone(run)} className="h-2" />
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-fg-muted">
          {(["running", "queued", "done", "failed", "superseded", "cancelled"] as const).map((s) =>
            run.mailboxes[s] ? (
              <span key={s} className="flex items-center gap-1.5">
                <StatusDot tone={MAILBOX_TONE[s]} pulse={s === "running"} />
                {t(`sync.run.mailboxCount.${s}`, { count: run.mailboxes[s] })}
              </span>
            ) : null,
          )}
        </div>
      </Card>

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Stat
          label={t("sync.run.created")}
          value={<span className="text-success">{formatNumber(run.created)}</span>}
        />
        <Stat label={t("sync.run.updated")} value={formatNumber(run.updated)} />
        <Stat
          label={t("sync.run.deleted")}
          value={<span className="text-danger">{formatNumber(run.deleted)}</span>}
        />
        <Stat
          label={t("sync.run.errors")}
          value={<span className={run.errors ? "text-warning" : undefined}>{formatNumber(run.errors)}</span>}
        />
        <Stat
          label={t("sync.run.requests")}
          value={formatNumber(calls)}
          hint={[
            photos ? t("sync.run.photos", { count: photos }) : null,
            throttled ? t("sync.run.throttled", { duration: formatDuration(t, throttled) }) : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        />
      </div>

      {!run.detailed ? (
        <Card className="px-5 py-4 text-[13px] text-fg-muted">{t("sync.run.noDetails")}</Card>
      ) : (
        <>
          <Tabs
            className="mb-5"
            value={tab}
            onValueChange={(v) => navigate({ search: (s) => ({ ...s, tab: v }) })}
            tabs={[
              { value: "mailboxes", label: t("sync.run.tabs.mailboxes"), count: mailboxes.length },
              { value: "changes", label: t("sync.run.tabs.changes") },
              { value: "log", label: t("sync.run.tabs.log") },
            ]}
          />
          {tab === "mailboxes" && <MailboxesTab detail={data} />}
          {tab === "changes" && <ChangesTab runId={runId} running={running} />}
          {tab === "log" && <LogTab runId={runId} running={running} />}
        </>
      )}
    </Page>
  );
}

function MailboxesTab({ detail }: { detail: SyncRunDetail }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Running first, then failed, waiting, done.
  const order: Record<SyncRunMailboxStatus, number> = {
    running: 0,
    failed: 1,
    queued: 2,
    done: 3,
    superseded: 4,
    cancelled: 5,
  };
  const rows = [...detail.mailboxes].sort((a, b) => order[a.status] - order[b.status]);
  return (
    <Card>
      <Table>
        <thead>
          <tr>
            <Th>{t("sync.run.columns.person")}</Th>
            <Th>{t("sync.run.columns.status")}</Th>
            <Th>{t("sync.run.columns.progress")}</Th>
            <Th className="hidden md:table-cell">{t("sync.columns.changes")}</Th>
            <Th className="hidden text-right lg:table-cell">{t("sync.run.columns.requests")}</Th>
            <Th className="hidden text-right sm:table-cell">{t("sync.run.columns.duration")}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => {
            const planned = m.plan.create + m.plan.update + m.plan.delete;
            const processed = m.done.created + m.done.updated + m.done.deleted + m.done.errors;
            const percent = m.status === "done" ? 100 : planned ? (processed / planned) * 100 : 0;
            return (
              <Tr
                key={m.userId}
                onClick={() => navigate({ to: "/people/$userId", params: { userId: m.userId } })}
              >
                <Td>
                  <div className="text-[13px] font-medium">{m.displayName}</div>
                  {m.email && <div className="text-[12px] text-fg-muted">{m.email}</div>}
                </Td>
                <Td>
                  <span className="flex items-center gap-1.5 whitespace-nowrap text-[12.5px]">
                    <StatusDot tone={MAILBOX_TONE[m.status]} pulse={m.status === "running"} />
                    {t(`sync.run.mailboxStatus.${m.status}`)}
                  </span>
                  {m.error && (
                    <div className="mt-0.5 max-w-64 truncate text-[12px] text-danger" title={m.error}>
                      {m.error}
                    </div>
                  )}
                </Td>
                <Td>
                  {m.status === "queued" ||
                  m.status === "superseded" ||
                  (m.status === "cancelled" && !planned) ? (
                    <span className="text-[12px] text-fg-subtle">-</span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <ProgressBar
                        percent={percent}
                        tone={
                          m.status === "failed"
                            ? "danger"
                            : m.status === "done"
                              ? "success"
                              : m.status === "cancelled"
                                ? "neutral"
                                : "accent"
                        }
                        className="w-24"
                      />
                      <span className="text-[12px] tabular-nums text-fg-muted">
                        {formatNumber(processed)}/{formatNumber(planned)}
                      </span>
                    </div>
                  )}
                </Td>
                <Td className="hidden font-mono text-[12px] text-fg-muted md:table-cell">
                  <span className="text-success">+{formatNumber(m.done.created)}</span> ~
                  {formatNumber(m.done.updated)}{" "}
                  <span className="text-danger">-{formatNumber(m.done.deleted)}</span>
                  {m.done.errors > 0 && <span className="text-warning"> !{formatNumber(m.done.errors)}</span>}
                </Td>
                <Td className="hidden text-right text-[12px] tabular-nums text-fg-muted lg:table-cell">
                  {formatNumber(m.calls)}
                  {m.throttledSeconds > 0 && (
                    <div className="text-warning">
                      {t("sync.run.throttled", { duration: formatDuration(t, m.throttledSeconds) })}
                    </div>
                  )}
                </Td>
                <Td className="hidden text-right text-[12px] tabular-nums text-fg-muted sm:table-cell">
                  {m.startedAt ? formatDuration(t, secondsBetween(m.startedAt, m.finishedAt)) : "-"}
                </Td>
              </Tr>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
}

const PAGE = 100;

function ChangesTab({ runId, running }: { runId: string; running: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [op, setOp] = useState<SyncChangeOp | "all">("all");
  const [query, setQuery] = useState("");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [limit, setLimit] = useState(PAGE);
  const q = useDebounced(query);
  const { data } = useQuery({
    queryKey: ["sync-run-changes", runId, op, q, errorsOnly, limit],
    queryFn: () =>
      api.get<SyncRunChanges>(
        `/api/sync/runs/${runId}/changes${qs({ op: op === "all" ? undefined : op, q: q || undefined, errors: errorsOnly ? 1 : undefined, limit })}`,
      ),
    placeholderData: keepPreviousData,
    refetchInterval: running ? POLL_MS * 2 : false,
  });
  const counts = data?.counts ?? { create: 0, update: 0, delete: 0 };
  const filters: { value: SyncChangeOp | "all"; count: number }[] = [
    { value: "all", count: counts.create + counts.update + counts.delete },
    { value: "create", count: counts.create },
    { value: "update", count: counts.update },
    { value: "delete", count: counts.delete },
  ];
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
        {filters.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => {
              setOp(f.value);
              setLimit(PAGE);
            }}
            className={cn(
              "rounded-md border px-2.5 py-1 text-[12.5px]",
              op === f.value
                ? "border-fg bg-subtle font-medium"
                : "border-border text-fg-muted hover:text-fg",
            )}
          >
            {t(`sync.run.ops.${f.value}`)} <span className="tabular-nums">{formatNumber(f.count)}</span>
          </button>
        ))}
        <label className="ml-1 flex items-center gap-1.5 text-[12.5px] text-fg-muted">
          <input type="checkbox" checked={errorsOnly} onChange={(e) => setErrorsOnly(e.target.checked)} />
          {t("sync.run.errorsOnly")}
        </label>
        <div className="relative ml-auto w-full sm:w-64">
          <MagnifyingGlassIcon className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-fg-subtle" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("sync.run.searchContact")}
            className="pl-8"
            aria-label={t("sync.run.searchContact")}
          />
        </div>
      </div>
      {!data ? (
        <Skeleton className="m-4 h-24" />
      ) : !data.items.length ? (
        <p className="px-5 py-4 text-[13px] text-fg-muted">{t("sync.run.noChanges")}</p>
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>{t("sync.run.columns.contact")}</Th>
                <Th>{t("sync.run.columns.change")}</Th>
                <Th className="hidden sm:table-cell">{t("sync.run.columns.reason")}</Th>
                <Th className="text-right">{t("sync.run.columns.mailboxes")}</Th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => (
                <Tr
                  key={`${c.op}:${c.contactId ?? c.label}`}
                  onClick={
                    c.contactId && c.op !== "delete"
                      ? () => navigate({ to: "/contacts", search: { contact: c.contactId! } })
                      : undefined
                  }
                >
                  <Td className="text-[13px] font-medium">{c.label || t("sync.run.unknownContact")}</Td>
                  <Td>
                    <Badge tone={c.op === "create" ? "success" : c.op === "delete" ? "danger" : "neutral"}>
                      {t(`sync.run.ops.${c.op}`)}
                    </Badge>
                  </Td>
                  <Td className="hidden text-[12.5px] text-fg-muted sm:table-cell">
                    {c.reason ? t(`sync.run.reasons.${c.reason}`, { defaultValue: c.reason }) : ""}
                  </Td>
                  <Td className="text-right text-[12.5px] tabular-nums">
                    {t("sync.run.inMailboxes", { count: c.mailboxes })}
                    {c.errors > 0 && (
                      <div className="text-danger">{t("sync.run.failedIn", { count: c.errors })}</div>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          {data.items.length < data.total && (
            <div className="flex items-center justify-between border-t border-border px-4 py-3 text-[12.5px] text-fg-muted">
              {t("sync.run.shown", {
                shown: formatNumber(data.items.length),
                total: formatNumber(data.total),
              })}
              <Button
                size="sm"
                onClick={() => setLimit((l) => Math.min(500, l + PAGE))}
                disabled={limit >= 500}
              >
                {t("sync.run.more")}
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

const LEVEL_TONE = { info: "neutral", warn: "warning", error: "danger" } as const;

function LogTab({ runId, running }: { runId: string; running: boolean }) {
  const { t } = useTranslation();
  const [events, setEvents] = useState<SyncRunEvent[]>([]);
  const [level, setLevel] = useState<"all" | "warn" | "error">("all");
  const last = useRef(0);

  // Loads the log once, then only newer entries while the run is going.
  useEffect(() => {
    let cancelled = false;
    last.current = 0;
    setEvents([]);
    const load = async () => {
      const fresh = await api.get<SyncRunEvent[]>(
        `/api/sync/runs/${runId}/events${qs({ after: last.current })}`,
      );
      if (cancelled || !fresh.length) return;
      last.current = fresh.at(-1)!.id;
      setEvents((e) => [...e, ...fresh]);
    };
    void load();
    const timer = running ? setInterval(() => void load(), POLL_MS) : undefined;
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [runId, running]);

  const shown = events.filter(
    (e) => level === "all" || e.level === "error" || (level === "warn" && e.level === "warn"),
  );
  const time = new Intl.DateTimeFormat(locale(), { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
        {(["all", "warn", "error"] as const).map((l) => (
          <button
            key={l}
            type="button"
            onClick={() => setLevel(l)}
            className={cn(
              "rounded-md border px-2.5 py-1 text-[12.5px]",
              level === l ? "border-fg bg-subtle font-medium" : "border-border text-fg-muted hover:text-fg",
            )}
          >
            {t(`sync.run.levels.${l}`)}
          </button>
        ))}
        {running && (
          <span className="ml-auto flex items-center gap-1.5 text-[12px] text-fg-muted">
            <StatusDot tone="accent" pulse />
            {t("sync.run.live")}
          </span>
        )}
      </div>
      {!shown.length ? (
        <p className="px-5 py-4 text-[13px] text-fg-muted">{t("sync.run.noEvents")}</p>
      ) : (
        <ol className="divide-y divide-border font-mono text-[12px]">
          {shown.map((e) => (
            <li key={e.id} className="flex gap-3 px-4 py-2">
              <span className="shrink-0 text-fg-subtle tabular-nums" title={formatDateTime(e.at)}>
                {time.format(new Date(e.at))}
              </span>
              <span className="mt-1 flex shrink-0">
                <StatusDot tone={LEVEL_TONE[e.level]} />
              </span>
              <span
                className={cn(
                  "min-w-0 break-words",
                  e.level === "error" ? "text-danger" : e.level === "warn" ? "text-warning" : "text-fg",
                )}
              >
                {eventText(t, e)}
              </span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

/** A log entry as a sentence; unknown codes are shown raw so nothing is hidden. */
function eventText(t: TFunction, e: SyncRunEvent): string {
  const d = e.data ?? {};
  const values: Record<string, string | number> = { name: e.displayName ?? e.userId ?? "" };
  for (const [k, v] of Object.entries(d)) values[k] = typeof v === "number" ? formatNumber(v) : (v ?? "");
  switch (e.code) {
    case "run_started":
      values.trigger = t(`sync.triggers.${d.trigger}`, { defaultValue: String(d.trigger ?? "") });
      break;
    case "mailbox_done":
    case "mailbox_cancelled":
    case "run_finished":
    case "mailbox_failed":
      values.duration = formatDuration(t, Number(d.seconds ?? 0));
      values.throttled = Number(d.throttledSeconds ?? 0)
        ? t("sync.run.throttledShort", { duration: formatDuration(t, Number(d.throttledSeconds)) })
        : "";
      if (e.code === "mailbox_failed")
        values.reason = t(`reasons.${d.reason}`, { defaultValue: String(d.reason ?? "") });
      break;
    case "mailbox_throttled":
      values.duration = formatDuration(t, Number(d.seconds ?? 0));
      break;
    case "contact_failed":
      values.opText = t(`sync.run.ops.${d.op}`, { defaultValue: String(d.op ?? "") });
      break;
  }
  const key = `sync.run.events.${e.code}`;
  return t(key, { ...values, defaultValue: `${e.code} ${JSON.stringify(d)}` });
}
